// Vue « Contacts » : ma journée, meilleur prochain créneau, frise 24 h calée sur mon fuseau,
// et pour chaque contact son heure locale, son statut et l’état de notre fenêtre commune.
// Un clic sur un contact ouvre ses créneaux ; un curseur permet d’explorer la journée (simulation).
import { initials, label, placeOf, status, sublabel, type Contact, type SortKey, type Status } from '../core/contact';
import { bestNext, dstAhead, type BestNext, type Slot } from '../core/schedule';
import { contactBand, dayGrid, hourLabels, meBand, nowFraction, type DayGrid } from '../core/timeline';
import { MIN, STEP, gapText, hm, info, nextDate, normalize, offsetText, pad, prettyDate, time, todayIn } from '../core/time';
import { commonWindow, durationText } from '../core/window';
import type { Store } from '../data/store';
import { el, esc, fmtClock, on } from './dom';
import { icon } from './icons';
import { syncBanner } from './shell';

type Tab = 'all' | 'client' | 'supplier' | 'favorites';

export interface ContactsCtx {
  openEditor(id: string | null): void;
  openImport(): void;
  openSettings(): void;
  openPlanner(ids: string[], date?: string): void;
  openLink(slots: Slot[]): void;
}

export interface MountedView {
  update(): void;
  tick?(now: number): void;
  /** Revenir au direct (Échap). */
  goLive?(): void;
}

const DAYS = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
function daysText(days: number[]): string {
  const sorted = [1, 2, 3, 4, 5, 6, 0].filter((d) => days.includes(d));
  if (sorted.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d))) return 'lun–ven';
  if (sorted.length === 7) return 'tous les jours';
  return sorted.map((d) => DAYS[d]).join(', ');
}

const STATE_TEXT: Record<Status, string> = { work: 'Au travail', awake: 'Hors horaires', night: 'Nuit' };
const SLOT_TITLE: Record<string, string> = { both: 'vous êtes disponibles tous les deux', work: 'lui seul travaille', awake: 'hors horaires', night: 'nuit' };
/** Durée proposée par défaut depuis la frise ; modifiable ensuite dans la fenêtre de proposition. */
const PROPOSE_MIN = 30;

export function mountContacts(main: HTMLElement, store: Store, ctx: ContactsCtx): MountedView {
  let tab: Tab = 'all';
  let query = '';
  /** Date affichée dans la frise, ou null pour aujourd’hui. */
  let viewDate: string | null = null;
  /** Instant simulé (curseur ou autre jour), ou null pour le direct. */
  let simTs: number | null = null;
  let currentGrid: DayGrid | null = null;
  let lastMinute = -1;
  let lastBest: BestNext | null = null;
  /** Bulle « Proposer ce créneau » ouverte depuis la frise, ou null. */
  let bubble: HTMLElement | null = null;
  const windowCache = new Map<string, string>();

  main.innerHTML = `
    <div data-banner></div>
    <div data-dst></div>
    <section class="page-head">
      <div><div class="eyebrow" data-today></div><h1>Contacts</h1><p data-sub></p></div>
      <div class="page-actions"><button class="btn" type="button" data-plan>${icon('calendar')}Planifier un appel</button><button class="btn primary" type="button" data-add>${icon('plus')}Ajouter un contact</button></div>
    </section>
    <section class="overview">
      <article class="me-card" aria-label="Ma journée">
        <div><div class="label">${icon('pin')}<span data-me-place></span><span data-me-mode>· maintenant</span></div>
          <div class="clock num"><span data-me-time>—</span><small data-me-sec></small></div>
          <div class="meta" data-me-meta></div></div>
        <div class="hours"><span>Mes horaires</span><b data-me-hours></b><span data-me-days></span></div>
      </article>
      <article class="best-card" aria-label="Meilleur prochain créneau" data-best></article>
    </section>
    <section class="panel" aria-label="Liste des contacts">
      <div class="toolbar">
        <div class="tabs" role="group" aria-label="Rubriques">
          <button class="tab" type="button" data-tab="all" aria-pressed="true">Tous <span class="count" data-count="all">0</span></button>
          <button class="tab" type="button" data-tab="client" aria-pressed="false">Clients <span class="count" data-count="client">0</span></button>
          <button class="tab" type="button" data-tab="supplier" aria-pressed="false">Fournisseurs <span class="count" data-count="supplier">0</span></button>
          <button class="tab" type="button" data-tab="favorites" aria-pressed="false">Favoris <span class="count" data-count="favorites">0</span></button>
        </div>
        <div class="filters">
          <label class="search">${icon('search')}<input type="search" data-search placeholder="Entreprise, personne, ville…" aria-label="Rechercher un contact, une entreprise ou une ville"></label>
          <select class="sort" data-sort aria-label="Trier les contacts"><option value="offset">Par fuseau</option><option value="name">Par nom</option><option value="availability">Par disponibilité</option></select>
        </div>
      </div>
      <div class="timeline-controls">
        <div class="date-nav">
          <button class="icon-btn" type="button" data-day="-1" aria-label="Jour précédent">${icon('chevron', 'flip')}</button>
          <input type="date" data-date aria-label="Jour affiché dans la frise">
          <button class="icon-btn" type="button" data-day="1" aria-label="Jour suivant">${icon('chevron')}</button>
          <button class="btn small live-btn" type="button" data-live-btn hidden>${icon('clock')}Revenir au direct</button>
          <span class="hint" data-grid-note></span>
        </div>
        <div class="legend" aria-label="Légende"><span><i class="both"></i>Ensemble</span><span><i class="work"></i>Lui seul</span><span><i class="awake"></i>Hors horaires</span><span><i class="night"></i>Nuit</span></div>
      </div>
      <div data-list class="list" data-live="true"></div>
      <div class="scrubber">
        <label class="scrub-label" for="scrub">${icon('clock')}Explorer la journée</label>
        <input class="scrub-range" id="scrub" type="range" min="0" max="95" step="1" data-scrub aria-label="Heure simulée dans mon fuseau">
        <output class="scrub-time num" for="scrub" data-scrub-time></output>
      </div>
      <div class="list-foot"><span>Frise à l’heure de <b data-foot-place></b>. Cliquez sur un contact pour voir vos créneaux, sur sa frise pour proposer l’heure choisie.</span><span data-readat></span></div>
    </section>`;

  const listEl = el(main, '[data-list]');
  const searchEl = el<HTMLInputElement>(main, '[data-search]');
  const sortEl = el<HTMLSelectElement>(main, '[data-sort]');
  const dateEl = el<HTMLInputElement>(main, '[data-date]');
  const scrubEl = el<HTMLInputElement>(main, '[data-scrub]');

  const activeTs = (): number => simTs ?? Date.now();
  const isLive = (): boolean => simTs === null && viewDate === null;
  function currentDate(ts = activeTs()): string { return viewDate ?? todayIn(store.state.profile.tz, ts); }

  function setDate(date: string): void {
    const me = store.state.profile;
    if (date === todayIn(me.tz, Date.now())) { viewDate = null; simTs = null; }
    else {
      viewDate = date;
      try { const g = dayGrid(date, me.tz); simTs = g.start + Math.min(12 * 3_600_000, g.end - g.start - STEP); }
      catch { viewDate = null; simTs = null; }
    }
    update();
  }
  function goLive(): void { viewDate = null; simTs = null; update(); }
  function simulate(ts: number): void {
    simTs = ts;
    windowCache.clear();
    paintMode();
    tick(Date.now());
  }

  // Bulle d’action au clic sur la frise d’un contact : proposer directement l’heure cliquée, sans
  // remonter en haut de page. Posée sur <body> en position fixe : `.band` et `.panel` rognent leur
  // contenu (overflow), une bulle placée à l’intérieur y serait coupée.
  const onDocClick = (e: MouseEvent): void => {
    const t = e.target as Element;
    if (!t.closest('.slot-bubble') && !t.closest('.slot')) closeBubble();
  };
  function closeBubble(): boolean {
    if (!bubble) return false;
    document.removeEventListener('click', onDocClick);
    window.removeEventListener('scroll', closeBubble, true);
    window.removeEventListener('resize', closeBubble);
    bubble.remove();
    bubble = null;
    return true;
  }
  function openBubble(cell: HTMLElement, c: Contact, start: number): void {
    closeBubble();
    const me = store.state.profile;
    const end = start + PROPOSE_MIN * MIN;
    const b = document.createElement('div');
    b.className = 'slot-bubble';
    b.innerHTML = `<strong class="num">${esc(time(me.tz, start))} – ${esc(time(me.tz, end))}</strong>
      <small>${esc(label(c))} · ${esc(time(c.tz, start))} à ${esc(placeOf(c))}</small>
      <button class="btn small primary" type="button" data-propose-slot>${icon('arrow')}Proposer ce créneau</button>`;
    document.body.append(b);
    el(b, '[data-propose-slot]').addEventListener('click', () => { closeBubble(); ctx.openLink([{ start, end, people: [c] }]); });
    const r = cell.getBoundingClientRect(), box = b.getBoundingClientRect();
    const above = r.top - box.height - 10;
    b.style.left = `${Math.round(Math.min(Math.max(8, r.left + r.width / 2 - box.width / 2), window.innerWidth - box.width - 8))}px`;
    b.style.top = `${Math.round(above >= 8 ? above : r.bottom + 10)}px`;
    bubble = b;
    document.addEventListener('click', onDocClick);
    window.addEventListener('scroll', closeBubble, true);
    window.addEventListener('resize', closeBubble);
  }

  on(main, 'click', '[data-add]', () => ctx.openEditor(null));
  on(main, 'click', '[data-plan]', () => ctx.openPlanner(visible(activeTs()).map((c) => c.id), viewDate ?? undefined));
  on(main, 'click', '[data-propose-best]', () => { if (lastBest) ctx.openLink([lastBest.slot]); });
  on(main, 'click', '[data-plan-with]', (_e, t) => ctx.openPlanner([t.dataset.planWith!], viewDate ?? undefined));
  main.addEventListener('keydown', (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('[data-plan-with]');
    if (t && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); ctx.openPlanner([t.dataset.planWith!], viewDate ?? undefined); }
  });
  on(main, 'click', '[data-import]', () => ctx.openImport());
  on(main, 'click', '[data-settings]', () => ctx.openSettings());
  on(main, 'click', '[data-retry]', () => void store.retry());
  on(main, 'click', '[data-tab]', (_e, t) => { tab = t.dataset.tab as Tab; update(); });
  on(main, 'click', '[data-show-all]', () => { tab = 'all'; query = ''; searchEl.value = ''; update(); });
  on(main, 'click', '[data-edit]', (_e, t) => ctx.openEditor(t.dataset.edit!));
  on(main, 'click', '[data-fav]', (_e, t) => { void store.toggleFavorite(t.dataset.fav!).catch(() => { /* déjà signalé */ }); });
  on(main, 'click', '[data-day]', (_e, t) => { setDate(nextDate(currentDate(), Number(t.dataset.day))); });
  on(main, 'click', '[data-live-btn]', goLive);
  on(listEl, 'click', '.slot', (_e, t) => {
    if (!currentGrid) return;
    const i = [...t.parentElement!.querySelectorAll('.slot')].indexOf(t);
    if (i < 0) return;
    const start = currentGrid.start + i * STEP;
    simulate(start);
    renderBest(visible(activeTs()), activeTs());
    const id = t.closest<HTMLElement>('.row[data-id]')?.dataset.id;
    const c = id ? store.state.contacts.find((x) => x.id === id) : undefined;
    if (c) openBubble(t, c, start); else closeBubble();
  });
  dateEl.addEventListener('change', () => { if (/^\d{4}-\d{2}-\d{2}$/.test(dateEl.value)) setDate(dateEl.value); });
  scrubEl.addEventListener('input', () => { if (currentGrid) simulate(currentGrid.start + Number(scrubEl.value) * STEP); });
  scrubEl.addEventListener('change', () => renderBest(visible(activeTs()), activeTs()));
  searchEl.addEventListener('input', () => { query = searchEl.value; update(); });
  sortEl.addEventListener('change', () => { void store.saveProfile({ sort: sortEl.value as SortKey }).catch(() => { /* déjà signalé */ }); update(); });

  function visible(ts: number): Contact[] {
    const q = normalize(query).trim();
    const me = store.state.profile;
    const sort = (sortEl.value || me.sort) as SortKey;
    const list = store.active.filter((c) =>
      (tab === 'all' || (tab === 'favorites' ? c.favorite : c.role === tab)) &&
      (!q || normalize(`${c.org} ${c.name} ${c.tz} ${placeOf(c)} ${c.country}`).includes(q)));
    const rank: Record<Status, number> = { work: 0, awake: 1, night: 2 };
    const meta = new Map(list.map((c) => [c.id, { off: info(c.tz, ts).offset, rank: rank[status(c, ts)] }]));
    return list.sort((a, b) => {
      if (sort === 'name') return label(a).localeCompare(label(b), 'fr');
      const A = meta.get(a.id)!, B = meta.get(b.id)!;
      if (sort === 'availability' && A.rank !== B.rank) return A.rank - B.rank;
      return B.off - A.off || label(a).localeCompare(label(b), 'fr');
    });
  }

  function bandHtml(kinds: string[], grid: DayGrid, tz: string, who: string): string {
    const cells = kinds.map((k, i) => `<span class="slot ${k}" title="${esc(who)} · ${esc(time(tz, grid.start + i * STEP))} · ${esc(SLOT_TITLE[k] ?? k)}"></span>`).join('');
    return `<div class="band" role="img" style="--hours:${grid.hours}" aria-label="Frise de ${esc(who)}">${cells}<span class="marker" aria-hidden="true"></span></div>`;
  }

  function row(c: Contact, grid: DayGrid): string {
    const me = store.state.profile;
    const place = placeOf(c);
    const who = [sublabel(c), place + (c.country && c.country !== place ? ', ' + c.country : '')].filter(Boolean).join(' · ');
    return `<article class="row" data-id="${esc(c.id)}" data-tz="${esc(c.tz)}">
      <div class="identity" role="button" tabindex="0" data-plan-with="${esc(c.id)}" title="Voir les créneaux avec ${esc(label(c))}"><span class="avatar" style="background:${esc(c.color)}">${esc(initials(label(c)))}</span>
        <div class="who"><div class="org"><span>${esc(label(c))}</span></div>
        <div class="person" title="${esc(who)}">${c.role === 'supplier' ? '<span class="role-tag supplier">Fournisseur</span>' : ''}${esc(who)}</div></div></div>
      ${bandHtml(contactBand(grid, c, me), grid, c.tz, label(c))}
      <div class="when"><div class="local-time num"><span data-time>—</span><small data-sec></small></div><div class="state" data-state></div><div class="window" data-window></div></div>
      <div class="row-actions">
        <button class="star" type="button" data-fav="${esc(c.id)}" aria-pressed="${c.favorite}" aria-label="${c.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'} : ${esc(label(c))}">${icon('star')}</button>
        <button class="icon-btn" type="button" data-edit="${esc(c.id)}" aria-label="Modifier ${esc(label(c))}">${icon('edit')}</button>
      </div></article>`;
  }

  function meRow(grid: DayGrid): string {
    const me = store.state.profile;
    return `<article class="row me-row" aria-label="Ma journée">
      <div class="identity" role="button" tabindex="0" data-settings title="Modifier mes horaires"><span class="avatar">MOI</span><div class="who"><div class="org"><span>Ma journée</span></div><div class="person">${esc(placeOf(me))} · ${esc(hm(me.start))}–${esc(hm(me.end))} · ${esc(daysText(me.days))}</div></div></div>
      ${bandHtml(meBand(grid, me), grid, me.tz, 'moi')}
      <div class="when"><div class="state">Mes horaires</div><div class="window">${esc(hm(me.start))} – ${esc(hm(me.end))}</div></div>
      <div class="row-actions"><button class="icon-btn" type="button" data-settings aria-label="Modifier mes horaires">${icon('settings')}</button></div></article>`;
  }

  function rulerRow(grid: DayGrid): string {
    const labels = hourLabels(grid, grid.hours > 24 ? 4 : 3).map((l) => `<span class="ruler-label num" style="left:${l.left}%">${esc(l.text)}</span>`).join('');
    return `<div class="row ruler-row" aria-hidden="true"><span>Contact</span><div class="ruler">${labels}</div><span class="when">Heure locale</span><span></span></div>`;
  }

  function renderBest(list: Contact[], ts: number): void {
    const me = store.state.profile;
    const box = el(main, '[data-best]');
    if (!list.length) { lastBest = null; box.innerHTML = `<div class="label">${icon('spark')}Meilleur prochain créneau</div><div class="big">—</div><div class="meta">Ajoutez un contact pour trouver un créneau commun.</div>`; return; }
    const b = bestNext({ people: list, me, now: ts, ...(viewDate ? { date: viewDate } : {}) });
    lastBest = b;
    if (!b) { box.innerHTML = `<div class="label">${icon('spark')}Meilleur prochain créneau</div><div class="big">Aucun</div><div class="meta">Pas de créneau commun de 30 min dans les 7 prochains jours${viewDate ? ' à partir de cette date' : ''}. Vérifiez vos horaires et les leurs.</div>`; return; }
    const today = todayIn(me.tz, ts);
    const d = todayIn(me.tz, b.slot.start);
    const when = d === today ? (isLive() ? 'aujourd’hui' : 'ce jour-là') : d === nextDate(today) ? (isLive() ? 'demain' : 'le lendemain') : prettyDate(b.slot.start, me.tz);
    const names = b.slot.people.map((c) => label(c)).join(', ');
    const partial = b.slot.people.length < list.length ? ` · ${b.slot.people.length}/${list.length} contacts` : list.length > 1 ? ' · tous' : '';
    box.innerHTML = `<div class="label">${icon('spark')}Meilleur prochain créneau${isLive() ? '' : ' <span class="sim-badge">après l’heure simulée</span>'}</div>
      <div class="big num">${esc(time(me.tz, b.slot.start))} – ${esc(time(me.tz, b.slot.end))}<small>${esc(when)}</small></div>
      <div class="meta">${esc(names)} + vous · 30 min${esc(partial)}</div>
      <div class="card-actions"><button class="btn small primary" type="button" data-propose-best>${icon('arrow')}Proposer ce créneau</button></div>`;
  }

  function renderDst(now: number): void {
    const box = el(main, '[data-dst]');
    const d = dstAhead({ me: store.state.profile, contacts: store.active, now });
    if (!d) { box.innerHTML = ''; return; }
    const me = store.state.profile;
    const day = prettyDate(d.when, me.tz, true);
    let txt = d.meFrom !== d.meTo
      ? `<b>${esc(day)}</b> : passage à l’${d.meTo > d.meFrom ? 'heure d’été' : 'heure d’hiver'} à ${esc(placeOf(me))}.`
      : `<b>${esc(day)}</b> : changement d’heure chez certains de vos contacts.`;
    if (d.moved.length) {
      const three = d.moved.slice(0, 3).map((m) => `${esc(m.name)} ${esc(gapText(m.from))} → ${esc(gapText(m.to))}`).join(' · ');
      txt += ` Votre écart change : ${three}${d.moved.length > 3 ? ` · +${d.moved.length - 3}` : ''}.`;
    } else txt += ' Vos horaires se décalent, l’écart avec vos contacts reste le même.';
    box.innerHTML = `<div class="banner info" role="status">${icon('clock')}<div>${txt}</div></div>`;
  }

  /** Éléments qui dépendent du mode direct / simulation, sans reconstruire la liste. */
  function paintMode(): void {
    const me = store.state.profile;
    const ts = activeTs();
    const live = isLive();
    listEl.dataset.live = String(live);
    el(main, '[data-live-btn]').hidden = live;
    el(main, '[data-me-mode]').innerHTML = live ? '· maintenant' : `· <span class="sim-badge">simulation</span>`;
    el(main, '[data-today]').textContent = live ? prettyDate(ts, me.tz, true) : `Simulation · ${prettyDate(ts, me.tz, true)} · ${time(me.tz, ts)}`;
    if (currentGrid) {
      scrubEl.max = String(currentGrid.slots - 1);
      const v = Math.max(0, Math.min(currentGrid.slots - 1, Math.floor((ts - currentGrid.start) / STEP)));
      if (document.activeElement !== scrubEl || live) scrubEl.value = String(v);
      el(main, '[data-scrub-time]').textContent = time(me.tz, ts) + (live ? ' · en direct' : '');
    }
  }

  function update(): void {
    const ts = activeTs();
    const s = store.state;
    const me = s.profile;
    const date = currentDate(ts);
    closeBubble(); // la liste est reconstruite : la bulle ne pointerait plus sur rien
    windowCache.clear(); // les données ou mes horaires ont pu changer
    el(main, '[data-banner]').innerHTML = syncBanner(store);
    el(main, '[data-sub]').textContent = `Quand joindre chacun, à l’heure de ${placeOf(me)}.`;
    el(main, '[data-me-place]').textContent = placeOf(me);
    el(main, '[data-me-hours]').textContent = `${hm(me.start)} – ${hm(me.end)}`;
    el(main, '[data-me-days]').textContent = daysText(me.days);
    el(main, '[data-foot-place]').textContent = placeOf(me);
    if (sortEl.value !== me.sort && document.activeElement !== sortEl) sortEl.value = me.sort;
    if (document.activeElement !== dateEl) dateEl.value = date;
    const all = store.active;
    const counts: Record<Tab, number> = { all: all.length, client: all.filter((c) => c.role !== 'supplier').length, supplier: all.filter((c) => c.role === 'supplier').length, favorites: all.filter((c) => c.favorite).length };
    (Object.keys(counts) as Tab[]).forEach((k) => { el(main, `[data-count="${k}"]`).textContent = String(counts[k]); });
    main.querySelectorAll<HTMLElement>('[data-tab]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.tab === tab)));
    el(main, '[data-readat]').textContent = s.sync.readAt ? (s.sync.fromCache ? 'Copie locale · ' : 'Serveur lu à ') + fmtClock(s.sync.readAt) : '';
    renderDst(Date.now());

    try { currentGrid = dayGrid(date, me.tz); }
    catch { viewDate = null; simTs = null; currentGrid = dayGrid(todayIn(me.tz, Date.now()), me.tz); }
    const grid = currentGrid;
    el(main, '[data-grid-note]').textContent = grid.hours !== 24 ? `journée de ${grid.hours} h` : '';
    paintMode();

    if (s.sync.phase === 'loading' || s.sync.phase === 'boot') {
      listEl.innerHTML = `<div class="loading-rows" aria-busy="true"><div class="skeleton"></div><div class="skeleton"></div><div class="skeleton"></div><p class="hint">Chargement de vos contacts depuis le serveur…</p></div>`;
      renderBest([], ts);
      return;
    }
    if ((s.sync.phase === 'error' || s.sync.phase === 'offline') && !s.sync.fromCache && !all.length) {
      listEl.innerHTML = `<div class="empty">${icon(s.sync.phase === 'offline' ? 'offline' : 'alert')}<h3>Vos contacts ne peuvent pas être affichés.</h3><p>${esc(s.sync.message || 'Le serveur est injoignable.')} Rien n’a été perdu : vos données restent sur le serveur.</p><div class="actions"><button class="btn primary" type="button" data-retry>${icon('refresh')}Réessayer</button></div></div>`;
      renderBest([], ts);
      return;
    }
    if (store.canShowEmpty) {
      listEl.innerHTML = rulerRow(grid) + meRow(grid) + `<div class="empty">${icon('users')}<h3>Aucun contact pour le moment.</h3><p>Ajoutez votre premier contact, ou importez la sauvegarde de l’ancienne version.</p><div class="actions"><button class="btn primary" type="button" data-add>${icon('plus')}Ajouter un contact</button><button class="btn" type="button" data-import>${icon('upload')}Importer une sauvegarde</button></div></div>`;
      renderBest([], ts);
      tick(Date.now());
      return;
    }
    const list = visible(ts);
    renderBest(list, ts);
    if (!list.length) {
      listEl.innerHTML = rulerRow(grid) + meRow(grid) + `<div class="empty">${icon(tab === 'favorites' ? 'star' : 'search')}<h3>Aucun contact ici.</h3><p>${query ? 'Essayez un autre nom, une autre entreprise ou une autre ville.' : tab === 'favorites' ? 'Ajoutez une étoile aux contacts que vous joignez souvent.' : 'Cette rubrique est vide.'}</p><div class="actions"><button class="btn" type="button" data-show-all>Voir tous les contacts</button></div></div>`;
      tick(Date.now());
      return;
    }
    listEl.innerHTML = rulerRow(grid) + meRow(grid) + list.map((c) => row(c, grid)).join('');
    tick(Date.now());
  }

  function windowText(c: Contact, ts: number, mine: { date: string }): { text: string; open: boolean } {
    const me = store.state.profile;
    const w = commonWindow(me, c, ts);
    if (w.state === 'open') return { text: `Ensemble encore ${durationText(w.until - ts)}`, open: true };
    if (w.state === 'later') {
      const d = todayIn(me.tz, w.at);
      if (d === mine.date) return { text: `Ensemble à ${time(me.tz, w.at)}, pendant ${durationText(w.until - w.at)}`, open: false };
      if (d === nextDate(mine.date)) return { text: `Prochain créneau demain ${time(me.tz, w.at)}`, open: false };
      return { text: `Prochain créneau ${prettyDate(w.at, me.tz)} ${time(me.tz, w.at)}`, open: false };
    }
    return { text: 'Pas de créneau commun sous 7 jours', open: false };
  }

  function tick(now: number): void {
    const me = store.state.profile;
    const live = isLive();
    const ts = live ? now : simTs ?? now;
    const mine = info(me.tz, ts);
    el(main, '[data-me-time]').textContent = hm(mine.minutes);
    el(main, '[data-me-sec]').textContent = live ? ':' + pad(mine.s) : '';
    el(main, '[data-me-meta]').textContent = `${prettyDate(ts, me.tz)} · ${offsetText(mine.offset)}`;
    const minute = Math.floor(ts / 60_000);
    const newMinute = minute !== lastMinute;
    if (newMinute) { windowCache.clear(); lastMinute = minute; }
    if (currentGrid) {
      const f = nowFraction(currentGrid, ts);
      listEl.style.setProperty('--cursor', f === null ? '-10%' : (f * 100).toFixed(3) + '%');
    }
    if (live) {
      el(main, '[data-scrub-time]').textContent = time(me.tz, ts) + ' · en direct';
      if (document.activeElement !== scrubEl && currentGrid) scrubEl.value = String(Math.max(0, Math.min(currentGrid.slots - 1, Math.floor((ts - currentGrid.start) / STEP))));
      // Minuit est passé : la frise change de journée.
      if (listEl.querySelector('.ruler-row') && dateEl.value !== todayIn(me.tz, ts) && document.activeElement !== dateEl) { update(); return; }
    }
    for (const r of listEl.querySelectorAll<HTMLElement>('.row[data-id]')) {
      const c = store.state.contacts.find((x) => x.id === r.dataset.id);
      if (!c) continue;
      const p = info(c.tz, ts), st = status(c, ts);
      const delta = p.date === mine.date ? '' : p.date < mine.date ? ' · hier' : ' · demain';
      const gap = p.offset - mine.offset;
      el(r, '[data-time]').textContent = hm(p.minutes);
      el(r, '[data-sec]').textContent = live ? ':' + pad(p.s) : '';
      const stateEl = el(r, '[data-state]');
      stateEl.className = 'state ' + st;
      stateEl.innerHTML = `${esc(STATE_TEXT[st])}${esc(delta)} <span class="gap">· ${gap === 0 ? 'même heure' : esc(gapText(gap))}</span>`;
      el(r, '.local-time').title = `${prettyDate(ts, c.tz, true)} · ${offsetText(p.offset)} · ${hm(c.start)}–${hm(c.end)} ${daysText(c.days)}`;
      let w = windowCache.get(c.id);
      if (w === undefined) {
        const t = windowText(c, ts, mine);
        w = (t.open ? '1' : '0') + t.text;
        windowCache.set(c.id, w);
      }
      const open = w.startsWith('1');
      const winEl = el(r, '[data-window]');
      winEl.className = 'window' + (open ? ' open' : '');
      winEl.textContent = w.slice(1);
      // Repère au premier coup d’œil : encore joignable maintenant (vert) ou hors de portée (rouge).
      r.classList.toggle('reach-open', open);
      r.classList.toggle('reach-closed', !open);
    }
    if (newMinute && live && listEl.querySelector('.row[data-id]')) renderBest(visible(ts), ts);
  }

  update();
  // Échap ferme d’abord la bulle, et ne revient au direct que s’il n’y en avait pas.
  return { update, tick, goLive: () => { if (!closeBubble()) goLive(); } };
}
